import { useState, useMemo, useEffect } from 'react';
import { getFacturasEmitidas, getGastosCompras } from '../../../../firebase/facturacionUtils';
import { sanitizeAmount } from '../helpers';

export const useReportesFinanzas = () => {
    const hoy = new Date();
    const [selectedMonth, setSelectedMonth] = useState(hoy.getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(hoy.getFullYear());

    const mStr = String(hoy.getMonth() + 1).padStart(2, '0');
    const ultimoDiaMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).getDate();

    const [fechaDesde, setFechaDesde] = useState(`${hoy.getFullYear()}-${mStr}-01`);
    const [fechaHasta, setFechaHasta] = useState(`${hoy.getFullYear()}-${mStr}-${String(ultimoDiaMes).padStart(2, '0')}`);

    const [loadingProgress, setLoadingProgress] = useState({ active: false, step: 0, text: '' });
    const [isDbLoaded, setIsDbLoaded] = useState(false);
    const [hasQueried, setHasQueried] = useState(false);
    const [queryError, setQueryError] = useState(null);

    const [moduloRaiz, setModuloRaiz] = useState('LIBRO_IVA'); // 'LIBRO_IVA' | 'CONCILIACIONES' | 'CASH_FLOW' | 'RETENCIONES'
    const [subVista, setSubVista] = useState('DEBITO'); 
    const [activeTab, setActiveTab] = useState('TABLA'); 
    const [busquedaGlobal, setBusquedaGlobal] = useState('');

    const [facturas, setFacturas] = useState([]);
    const [gastos, setGastos] = useState([]);

    const [multiFiltros, setMultiFiltros] = useState({
        moneda: 'TODAS',
        areaId: 'TODAS'
    });

    const [columnasSeleccionadas, setColumnasSeleccionadas] = useState([]);

    // CONSULTA A FIRESTORE BAJO DEMANDA (SÓLO AL HACER CLIC EN "FILTRAR")
    const consultarReporte = async (e) => {
        if (e) e.preventDefault();
        setLoadingProgress({ active: true, step: 1, text: 'Sincronizando comprobantes...' });
        setIsDbLoaded(false);
        setQueryError(null);

        try {
            const [facturasData, gastosData] = await Promise.all([
                getFacturasEmitidas(),
                getGastosCompras()
            ]);

            setFacturas(facturasData || []);
            setGastos(gastosData || []);
            setIsDbLoaded(true);
            setHasQueried(true);
        } catch (err) {
            console.error("Error al cargar datos financieros:", err);
            setQueryError(err.message || "Error al cargar la base de datos.");
            setIsDbLoaded(true);
            setHasQueried(true);
        } finally {
            setLoadingProgress({ active: false, step: 0, text: '' });
        }
    };

    // Sub-vista activa por defecto según el módulo financiero
    useEffect(() => {
        if (moduloRaiz === 'LIBRO_IVA') setSubVista('DEBITO');
        else if (moduloRaiz === 'CONCILIACIONES') setSubVista('COBROS');
        else if (moduloRaiz === 'CASH_FLOW') setSubVista('INGRESOS');
        else if (moduloRaiz === 'RETENCIONES') setSubVista('TODAS');
    }, [moduloRaiz]);

    // Período de fechas límite
    const periodoFechas = useMemo(() => {
        const mStrP = String(selectedMonth).padStart(2, '0');
        const startDateKey = `${selectedYear}-${mStrP}-01`;
        const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
        const endDateKey = `${selectedYear}-${mStrP}-${String(lastDay).padStart(2, '0')}`;
        const periodoFormatoMatch = `${mStrP}/${selectedYear}`;

        return { startDateKey, endDateKey, periodoFormatoMatch, lastDay };
    }, [selectedYear, selectedMonth]);

    // Filtrado base del mes
    const { facturasMes, gastosMes } = useMemo(() => {
        if (!hasQueried) return { facturasMes: [], gastosMes: [] };

        const facturasFiltradas = facturas.filter(f => {
            if (f.estatus === 'Anulada') return false;
            const fecha = (f.fechaEmision || f.fechaIngreso || '').substring(0, 10);
            const coincidePeriodo = (fecha >= periodoFechas.startDateKey && fecha <= periodoFechas.endDateKey) || f.periodo === periodoFechas.periodoFormatoMatch;
            const coincideMoneda = multiFiltros.moneda === 'TODAS' || (f.moneda || 'ARS') === multiFiltros.moneda;
            const coincideArea = multiFiltros.areaId === 'TODAS' || f.areaId === multiFiltros.areaId;
            
            return coincidePeriodo && coincideMoneda && coincideArea;
        });

        const gastosFiltrados = gastos.filter(g => {
            const fecha = (g.fechaGasto || g.fechaIngreso || '').substring(0, 10);
            const coincidePeriodo = (fecha >= periodoFechas.startDateKey && fecha <= periodoFechas.endDateKey) || g.periodo === periodoFechas.periodoFormatoMatch;
            const coincideMoneda = multiFiltros.moneda === 'TODAS' || (g.moneda || 'ARS') === multiFiltros.moneda;
            const coincideArea = multiFiltros.areaId === 'TODAS' || g.areaId === multiFiltros.areaId;

            return coincidePeriodo && coincideMoneda && coincideArea;
        });

        return { facturasMes: facturasFiltradas, gastosMes: gastosFiltrados };
    }, [hasQueried, facturas, gastos, periodoFechas, multiFiltros]);

    // 1. KPIS LIBRO IVA
    const acumuladosIVA = useMemo(() => {
        if (!hasQueried) return null;

        let totalIvaDebitoARS = 0; let totalIvaDebitoUSD = 0;
        let totalIvaCreditoARS = 0; let totalIvaCreditoUSD = 0;
        let totalRetencionesIvaARS = 0; let totalRetencionesIvaUSD = 0;

        facturasMes.forEach(f => {
            const mon = f.moneda || 'ARS';
            const iva = sanitizeAmount(f.montoIva);
            if (mon === 'USD') totalIvaDebitoUSD += iva;
            else totalIvaDebitoARS += iva;

            if (Array.isArray(f.retencionesList)) {
                f.retencionesList.forEach(ret => {
                    const tipo = (ret.tipo || '').trim().toUpperCase();
                    if (tipo === 'IVA' || tipo === 'RETENCION IVA' || tipo === 'RETENCIÓN IVA') {
                        const monto = sanitizeAmount(ret.monto);
                        if (mon === 'USD') totalRetencionesIvaUSD += monto;
                        else totalRetencionesIvaARS += monto;
                    }
                });
            }
        });

        const gastosFacturaA = gastosMes.filter(g => (g.tipoComprobante || g.tipoComprobanteGasto || '').trim() === 'Factura A');
        gastosFacturaA.forEach(g => {
            const mon = g.moneda || 'ARS';
            const iva = sanitizeAmount(g.montoIva);
            if (mon === 'USD') totalIvaCreditoUSD += iva;
            else totalIvaCreditoARS += iva;
        });

        return { 
            totalIvaDebitoARS, totalIvaDebitoUSD, 
            totalIvaCreditoARS, totalIvaCreditoUSD, 
            totalRetencionesIvaARS, totalRetencionesIvaUSD,
            posicionIvaARS: totalIvaDebitoARS - totalIvaCreditoARS - totalRetencionesIvaARS,
            posicionIvaUSD: totalIvaDebitoUSD - totalIvaCreditoUSD - totalRetencionesIvaUSD
        };
    }, [hasQueried, facturasMes, gastosMes]);

    // 2. AUDITORÍA DE CONCILIACIONES
    const reportesConciliacion = useMemo(() => {
        if (!hasQueried) return null;

        let montoTotalNoConciliadoARS = 0;
        let montoTotalNoConciliadoUSD = 0;
        let comprobantesConDesfaseCount = 0;

        const facturasConDesfase = facturasMes.map(f => {
            const mon = f.moneda || 'ARS';
            const total = sanitizeAmount(f.montoTotal);
            const retenciones = sanitizeAmount(f.retencionesTotales);
            const esperado = total - retenciones;
            const ingresado = sanitizeAmount(f.montoIngresoBanco);
            
            const diferencia = f.estatus === 'Cobrada' || ingresado > 0 
                ? Math.round((ingresado - esperado) * 100) / 100 
                : 0;
            
            const tieneDiferencia = Math.abs(diferencia) > 0.01;

            if (tieneDiferencia) {
                if (mon === 'USD') montoTotalNoConciliadoUSD += Math.abs(diferencia);
                else montoTotalNoConciliadoARS += Math.abs(diferencia);
                comprobantesConDesfaseCount++;
            }

            return { ...f, moneda: mon, total, esperado, ingresado, diferencia, tieneDiferencia };
        });

        const gastosConDesfase = gastosMes.map(g => {
            const mon = g.moneda || 'ARS';
            const total = sanitizeAmount(g.montoTotal);
            const tienePlan = g.esPlanPago && Array.isArray(g.cuotasPlan) && g.cuotasPlan.length > 0;
            const tieneCheques = Array.isArray(g.chequesList) && g.chequesList.length > 0;

            let egresoBanco = 0;

            if (tienePlan || tieneCheques) {
                const pagosDirectos = (Array.isArray(g.pagosList) ? g.pagosList : [])
                    .filter(p => p.formaPago !== 'Cheque')
                    .reduce((acc, p) => acc + sanitizeAmount(p.monto), 0);

                const chequesCobrados = (Array.isArray(g.chequesList) ? g.chequesList : [])
                    .filter(chk => chk.cobrado)
                    .reduce((acc, chk) => acc + sanitizeAmount(chk.monto), 0);

                const cuotasPagadas = (Array.isArray(g.cuotasPlan) ? g.cuotasPlan : [])
                    .filter(c => c.pagada)
                    .reduce((acc, c) => acc + sanitizeAmount(c.monto), 0);

                egresoBanco = Math.round((pagosDirectos + chequesCobrados + cuotasPagadas) * 100) / 100;
            } else {
                egresoBanco = sanitizeAmount(g.montoEgresoBanco);
            }

            const esperado = total;
            const diferencia = g.forzarConciliacion 
                ? 0 
                : Math.round((egresoBanco - total) * 100) / 100;
            
            const tieneDiferencia = Math.abs(diferencia) > 0.01 && !g.forzarConciliacion;

            if (tieneDiferencia) {
                if (mon === 'USD') montoTotalNoConciliadoUSD += Math.abs(diferencia);
                else montoTotalNoConciliadoARS += Math.abs(diferencia);
                comprobantesConDesfaseCount++;
            }

            return { ...g, moneda: mon, total, esperado, egresoBanco, diferencia, tieneDiferencia };
        });

        const totalOperaciones = facturasMes.length + gastosMes.length;
        const porcentajeSaludConciliacion = totalOperaciones > 0
            ? Math.max(0, Math.round(((totalOperaciones - comprobantesConDesfaseCount) / totalOperaciones) * 100))
            : 100;

        return { 
            facturasConDesfase, 
            gastosConDesfase, 
            montoTotalNoConciliadoARS,
            montoTotalNoConciliadoUSD, 
            comprobantesConDesfaseCount, 
            porcentajeSaludConciliacion,
            totalOperaciones
        };
    }, [hasQueried, facturasMes, gastosMes]);

    // 3. MOTOR DE CASH FLOW BANCO
    const cashFlowData = useMemo(() => {
        if (!hasQueried) return null;

        const ingresosDetalle = [];
        let totalIngresosBancoARS = 0;
        let totalIngresosBancoUSD = 0;

        facturasMes.forEach(f => {
            const mon = f.moneda || 'ARS';
            const montoIngresado = sanitizeAmount(f.montoIngresoBanco);
            if (f.estatus === 'Cobrada' || montoIngresado > 0) {
                if (mon === 'USD') totalIngresosBancoUSD += montoIngresado;
                else totalIngresosBancoARS += montoIngresado;

                ingresosDetalle.push({
                    id: f.id,
                    fecha: (f.fechaEmision || f.fechaIngreso || '').substring(0, 10),
                    entidad: f.clienteNombre || 'S/D',
                    numeroFactura: `${f.tipoComprobante || 'Factura'} N° ${f.numeroFactura || 'S/N'}`,
                    tipoComprobante: 'Cobro Cliente',
                    areaNombre: f.areaNombre || 'S/A',
                    moneda: mon,
                    montoTotal: montoIngresado
                });
            }
        });

        const egresosDetalle = [];
        let totalEgresosBancoARS = 0;
        let totalEgresosBancoUSD = 0;

        gastosMes.forEach(g => {
            const mon = g.moneda || 'ARS';
            const tienePlan = g.esPlanPago && Array.isArray(g.cuotasPlan) && g.cuotasPlan.length > 0;
            const tieneCheques = Array.isArray(g.chequesList) && g.chequesList.length > 0;

            let egresoEfectivoReal = 0;

            if (tienePlan || tieneCheques) {
                const pagosDirectos = (Array.isArray(g.pagosList) ? g.pagosList : [])
                    .filter(p => p.formaPago !== 'Cheque')
                    .reduce((acc, p) => acc + sanitizeAmount(p.monto), 0);

                const chequesCobrados = (Array.isArray(g.chequesList) ? g.chequesList : [])
                    .filter(chk => chk.cobrado)
                    .reduce((acc, chk) => acc + sanitizeAmount(chk.monto), 0);

                const cuotasPagadas = (Array.isArray(g.cuotasPlan) ? g.cuotasPlan : [])
                    .filter(c => c.pagada)
                    .reduce((acc, c) => acc + sanitizeAmount(c.monto), 0);

                egresoEfectivoReal = Math.round((pagosDirectos + chequesCobrados + cuotasPagadas) * 100) / 100;
            } else {
                egresoEfectivoReal = sanitizeAmount(g.montoEgresoBanco);
            }

            if (egresoEfectivoReal > 0) {
                if (mon === 'USD') totalEgresosBancoUSD += egresoEfectivoReal;
                else totalEgresosBancoARS += egresoEfectivoReal;

                egresosDetalle.push({
                    id: g.id,
                    fecha: (g.fechaEgresoBanco || g.fechaGasto || g.fechaIngreso || '').substring(0, 10),
                    entidad: g.proveedorNombre || 'S/D',
                    numeroFactura: `${g.tipoComprobante || 'Gasto'} N° ${g.numeroComprobante || 'S/N'}`,
                    tipoComprobante: g.conceptoNombre || 'Gasto Operativo',
                    areaNombre: g.areaNombre || 'S/A',
                    moneda: mon,
                    montoTotal: egresoEfectivoReal
                });
            }
        });

        return { 
            totalIngresosBancoARS,
            totalIngresosBancoUSD, 
            totalEgresosBancoARS,
            totalEgresosBancoUSD, 
            saldoCashFlowARS: totalIngresosBancoARS - totalEgresosBancoARS,
            saldoCashFlowUSD: totalIngresosBancoUSD - totalEgresosBancoUSD, 
            ingresosDetalle, 
            egresosDetalle 
        };
    }, [hasQueried, facturasMes, gastosMes, periodoFechas]);

    // 4. MOTOR DE RETENCIONES SUFRIDAS
    const retencionesDiscriminadas = useMemo(() => {
        if (!hasQueried) return null;

        const desglosePorTipoARS = {};
        const desglosePorTipoUSD = {};
        const listaFlat = [];

        facturasMes.forEach(f => {
            const mon = f.moneda || 'ARS';
            if (Array.isArray(f.retencionesList)) {
                f.retencionesList.forEach((ret, idx) => {
                    const tipo = ret.tipo || 'Otras Retenciones';
                    const monto = sanitizeAmount(ret.monto);

                    if (mon === 'USD') {
                        if (!desglosePorTipoUSD[tipo]) desglosePorTipoUSD[tipo] = 0;
                        desglosePorTipoUSD[tipo] += monto;
                    } else {
                        if (!desglosePorTipoARS[tipo]) desglosePorTipoARS[tipo] = 0;
                        desglosePorTipoARS[tipo] += monto;
                    }

                    listaFlat.push({
                        id: `${f.id}-ret-${idx}`,
                        fecha: (f.fechaEmision || f.fechaIngreso || '').substring(0, 10),
                        entidad: f.clienteNombre || 'S/D',
                        areaNombre: f.areaNombre || 'S/A',
                        tipoComprobante: `${f.tipoComprobante || 'Factura'} N° ${f.numeroFactura || 'S/N'}`,
                        numeroFactura: tipo,
                        moneda: mon,
                        montoTotal: monto
                    });
                });
            }
        });

        const totalRetenidoARS = Object.values(desglosePorTipoARS).reduce((a, b) => a + b, 0);
        const totalRetenidoUSD = Object.values(desglosePorTipoUSD).reduce((a, b) => a + b, 0);

        return { desglosePorTipoARS, desglosePorTipoUSD, listaFlat, totalRetenidoARS, totalRetenidoUSD };
    }, [hasQueried, facturasMes]);

    // MAPEO DE REGISTROS PARA TablePreviewFinanzas
    const registrosProcesados = useMemo(() => {
        if (!hasQueried || !isDbLoaded) return [];
        let data = [];

        if (moduloRaiz === 'LIBRO_IVA') {
            if (subVista === 'DEBITO') {
                data = facturasMes.map(f => ({
                    id: f.id,
                    fecha: (f.fechaEmision || f.fechaIngreso || '').substring(0, 10),
                    entidad: f.clienteNombre || 'S/D',
                    areaNombre: f.areaNombre || 'S/A',
                    tipoComprobante: f.tipoComprobante || 'Factura',
                    numeroFactura: f.numeroFactura || 'S/N',
                    moneda: f.moneda || 'ARS',
                    montoIva: sanitizeAmount(f.montoIva),
                    montoTotal: sanitizeAmount(f.montoTotal)
                }));
            } else if (subVista === 'CREDITO') {
                data = gastosMes
                    .filter(g => (g.tipoComprobante || g.tipoComprobanteGasto || '').trim() === 'Factura A')
                    .map(g => ({
                        id: g.id,
                        fecha: (g.fechaGasto || g.fechaIngreso || '').substring(0, 10),
                        entidad: g.proveedorNombre || 'S/D',
                        areaNombre: g.areaNombre || 'S/A',
                        tipoComprobante: g.tipoComprobante || 'Factura A',
                        numeroFactura: g.numeroComprobante || 'S/N',
                        moneda: g.moneda || 'ARS',
                        montoIva: sanitizeAmount(g.montoIva),
                        montoTotal: sanitizeAmount(g.montoTotal)
                    }));
            } else if (subVista === 'RETENCIONES_IVA') {
                const list = [];
                facturasMes.forEach(f => {
                    if (Array.isArray(f.retencionesList)) {
                        f.retencionesList.forEach((ret, idx) => {
                            const tipo = (ret.tipo || '').trim().toUpperCase();
                            if (tipo === 'IVA' || tipo === 'RETENCION IVA' || tipo === 'RETENCIÓN IVA') {
                                list.push({
                                    id: `${f.id}-${idx}`,
                                    fecha: (f.fechaEmision || f.fechaIngreso || '').substring(0, 10),
                                    entidad: f.clienteNombre || 'S/D',
                                    areaNombre: f.areaNombre || 'S/A',
                                    tipoComprobante: f.tipoComprobante,
                                    numeroFactura: f.numeroFactura || 'S/N',
                                    moneda: f.moneda || 'ARS',
                                    montoIva: sanitizeAmount(ret.monto)
                                });
                            }
                        });
                    }
                });
                data = list;
            }

        } else if (moduloRaiz === 'CONCILIACIONES') {
            if (subVista === 'COBROS' && reportesConciliacion) {
                data = reportesConciliacion.facturasConDesfase.map(f => ({
                    id: f.id,
                    fecha: (f.fechaEmision || f.fechaIngreso || '').substring(0, 10),
                    entidad: f.clienteNombre || 'S/D',
                    areaNombre: f.areaNombre || 'S/A',
                    tipoComprobante: f.tipoComprobante || 'Factura',
                    numeroFactura: f.numeroFactura || 'S/N',
                    moneda: f.moneda,
                    montoTotal: f.montoTotal,
                    montoIva: f.retencionesTotales,
                    esperado: f.esperado,
                    ingresado: f.ingresado,
                    diferencia: f.diferencia,
                    tieneDiferencia: f.tieneDiferencia,
                    estadoAuditoria: f.estatus !== 'Cobrada' && !f.ingresado ? 'PENDIENTE' : 'EVALUADO'
                }));
            } else if (subVista === 'PAGOS' && reportesConciliacion) {
                data = reportesConciliacion.gastosConDesfase.map(g => ({
                    id: g.id,
                    fecha: (g.fechaGasto || g.fechaIngreso || '').substring(0, 10),
                    entidad: g.proveedorNombre || 'S/D',
                    areaNombre: g.areaNombre || 'S/A',
                    tipoComprobante: g.tipoComprobante || 'Gasto',
                    numeroFactura: g.numeroComprobante || 'S/N',
                    moneda: g.moneda,
                    montoTotal: g.total,
                    egresoBanco: g.egresoBanco,
                    diferencia: g.diferencia,
                    tieneDiferencia: g.tieneDiferencia,
                    estadoAuditoria: 'EVALUADO'
                }));
            }

        } else if (moduloRaiz === 'CASH_FLOW') {
            if (subVista === 'INGRESOS' && cashFlowData) {
                data = cashFlowData.ingresosDetalle;
            } else if (subVista === 'EGRESOS' && cashFlowData) {
                data = cashFlowData.egresosDetalle;
            }

        } else if (moduloRaiz === 'RETENCIONES' && retencionesDiscriminadas) {
            data = retencionesDiscriminadas.listaFlat;
        }

        if (busquedaGlobal) {
            const s = busquedaGlobal.toLowerCase();
            return data.filter(r => 
                String(r.entidad || '').toLowerCase().includes(s) ||
                String(r.numeroFactura || '').toLowerCase().includes(s)
            );
        }

        return data;
    }, [hasQueried, isDbLoaded, moduloRaiz, subVista, facturasMes, gastosMes, reportesConciliacion, cashFlowData, retencionesDiscriminadas, busquedaGlobal]);

    // Selección de columnas para TablePreviewFinanzas
    useEffect(() => {
        if (moduloRaiz === 'LIBRO_IVA') {
            if (subVista === 'DEBITO' || subVista === 'CREDITO') {
                setColumnasSeleccionadas(['fecha', 'entidad', 'areaNombre', 'tipoComprobante', 'numeroFactura', 'moneda', 'montoIva', 'montoTotal']);
            } else {
                setColumnasSeleccionadas(['fecha', 'entidad', 'areaNombre', 'tipoComprobante', 'numeroFactura', 'moneda', 'montoIva']);
            }
        } else if (moduloRaiz === 'CONCILIACIONES') {
            if (subVista === 'COBROS') {
                setColumnasSeleccionadas(['fecha', 'entidad', 'areaNombre', 'tipoComprobante', 'numeroFactura', 'moneda', 'montoTotal', 'montoIva', 'esperado', 'ingresado', 'estadoAuditoria']);
            } else {
                setColumnasSeleccionadas(['fecha', 'entidad', 'areaNombre', 'tipoComprobante', 'numeroFactura', 'moneda', 'montoTotal', 'egresoBanco', 'estadoAuditoria']);
            }
        } else if (moduloRaiz === 'CASH_FLOW') {
            setColumnasSeleccionadas(['fecha', 'entidad', 'areaNombre', 'tipoComprobante', 'numeroFactura', 'moneda', 'montoTotal']);
        } else if (moduloRaiz === 'RETENCIONES') {
            setColumnasSeleccionadas(['fecha', 'entidad', 'areaNombre', 'tipoComprobante', 'numeroFactura', 'moneda', 'montoTotal']);
        }
    }, [moduloRaiz, subVista]);

    const opcionesFiltrosUnicas = useMemo(() => {
        const areaMap = new Map();
        facturas.forEach(f => { if (f.areaId && f.areaNombre) areaMap.set(f.areaId, f.areaNombre); });
        gastos.forEach(g => { if (g.areaId && g.areaNombre) areaMap.set(g.areaId, g.areaNombre); });

        return {
            areas: Array.from(areaMap.entries()).map(([id, nombre]) => ({ id, nombre }))
        };
    }, [facturas, gastos]);

    const camposDisponibles = useMemo(() => ({
        'Finanzas': [
            { key: 'fecha', label: 'Fecha' },
            { key: 'entidad', label: 'Cliente / Proveedor' },
            { key: 'areaNombre', label: 'Área / C. Costo' },
            { key: 'tipoComprobante', label: 'Concepto / Comprobante AFIP' },
            { key: 'numeroFactura', label: 'Detalle / Tipo Retención' },
            { key: 'moneda', label: 'Moneda' },
            { key: 'montoIva', label: 'Monto IVA' },
            { key: 'montoTotal', label: 'Monto Retenido / Total' },
            { key: 'esperado', label: 'Cobro Esperado' },
            { key: 'ingresado', label: 'Acreditado CBU' },
            { key: 'egresoBanco', label: 'Debitado CBU' },
            { key: 'estadoAuditoria', label: 'Auditoría' }
        ]
    }), []);

    return {
        fechaDesde, setFechaDesde,
        fechaHasta, setFechaHasta,
        selectedMonth, setSelectedMonth,
        selectedYear, setSelectedYear,
        loadingProgress,
        isDbLoaded,
        hasQueried,
        queryError,
        moduloRaiz, setModuloRaiz,
        subVista, setSubVista,
        activeTab, setActiveTab,
        busquedaGlobal, setBusquedaGlobal,
        multiFiltros, setMultiFiltros,
        columnasSeleccionadas,
        registrosProcesados,
        opcionesFiltrosUnicas,
        consultarReporte,
        camposDisponibles,
        acumuladosIVA,
        reportesConciliacion,
        cashFlowData,
        retencionesDiscriminadas,
        facturasMes
    };
};